import React from 'react'
import { extractOriginalFilename, toKebabCase } from '~/utils/tarUtils'
import type { CanvasItem } from './message'
import { getFileExtension, getFileNameWithoutExtension } from './uploadCardShared'

export interface CanvasMessageCardProps {
  canvas: CanvasItem
  onClick?: () => void
}

/**
 * Get display info for a canvas item based on canvas type.
 * Extension detection hierarchy (never uses title):
 * - CANVAS_TYPE_IMAGE → PNG
 * - CANVAS_TYPE_MARKDOWN → MD
 * - CANVAS_TYPE_S3_FILE → from s3FilePath (Directory for tar.gz)
 */
function getCanvasDisplayInfo(canvas: CanvasItem): {
  displayName: string
  extension: string
  icon: 'folder' | 'image' | 'file'
} {
  const title = canvas.title || 'Untitled'

  switch (canvas.canvasType) {
    case 'CANVAS_TYPE_IMAGE':
      return {
        displayName: getFileNameWithoutExtension(title, false),
        extension: 'PNG',
        icon: 'image'
      }

    case 'CANVAS_TYPE_MARKDOWN':
      return {
        displayName: getFileNameWithoutExtension(title, false),
        extension: 'MD',
        icon: 'file'
      }

    case 'CANVAS_TYPE_S3_FILE':
    default: {
      const s3Path = canvas.s3FilePath || ''
      const isTarGz = s3Path.toLowerCase().endsWith('.tar.gz')

      if (isTarGz) {
        return {
          displayName: toKebabCase(title),
          extension: 'Directory',
          icon: 'folder'
        }
      }

      // Extract original filename from s3Path (handles device ID prefix)
      const originalFilename = extractOriginalFilename(s3Path)
      return {
        displayName: getFileNameWithoutExtension(title, false),
        extension: getFileExtension(originalFilename).toUpperCase() || 'FILE',
        icon: 'file'
      }
    }
  }
}

/**
 * Card component for displaying a canvas file/artifact in the chat.
 * Editable canvas items show a special indicator and different styling.
 */
export const CanvasMessageCard: React.FC<CanvasMessageCardProps> = ({ canvas, onClick }) => {
  const { displayName, extension, icon } = getCanvasDisplayInfo(canvas)
  const isEditable = canvas.editable === true

  return (
    <div
      onClick={onClick}
      className={`group flex items-start cursor-pointer transition-all rounded-[20px] p-4 gap-4 ${
        isEditable
          ? 'bg-tokens-status-link-base-2 border border-[rgba(91,91,213,0.2)] hover:bg-tokens-neutral-bg-grey-3'
          : 'bg-tokens-neutral-bg-grey-1 border border-tokens-neutral-border-colored'
      }`}
      style={{
        width: 'min(360px, 100%)',
        boxShadow:
          '0px 2px 4px 0px rgba(0,0,0,0.02), 0px 1px 6px -1px rgba(0,0,0,0.02), 0px 1px 2px 0px rgba(0,0,0,0.03)'
      }}
      title={isEditable ? `Click to edit ${displayName}` : `Click to view ${displayName}`}
    >
      <div className='flex items-center p-1 shrink-0'>
        {isEditable ? (
          <EditableIcon />
        ) : (
          <>
            {icon === 'folder' && <FolderIcon />}
            {icon === 'image' && <ImageIcon />}
            {icon === 'file' && <FileIcon />}
          </>
        )}
      </div>

      <div className='flex flex-col items-start flex-1 min-w-0'>
        <p className='font-geist font-medium text-[14px] leading-[22px] text-tokens-neutral-text-primary truncate w-full'>
          {displayName}
        </p>
        <p className='font-geist font-medium text-[12px] leading-[16px] text-tokens-neutral-text-tertiary whitespace-nowrap'>
          {extension}
        </p>
      </div>

      {isEditable && (
        <>
          {/* Default: Editable pill tag */}
          <span className='shrink-0 px-[8px] py-[4px] text-[10px] font-bold font-geist text-tokens-status-link-3 bg-tokens-status-link-base rounded-[50px] group-hover:hidden'>
            Editable
          </span>
          {/* Hover: Chevron right */}
          <CardChevronRight className='shrink-0 hidden group-hover:block self-center text-[#8E8E8E]' />
        </>
      )}
    </div>
  )
}

const CardChevronRight: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    width='24'
    height='24'
    viewBox='0 0 24 24'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'
    className={className}
  >
    <path
      d='M9 6L15 12L9 18'
      stroke='currentColor'
      strokeWidth='1.5'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
  </svg>
)

const FolderIcon = () => (
  <div className='flex items-center justify-center rounded-lg bg-tokens-primary-2 p-1'>
    <svg width='18' height='18' viewBox='0 0 18 18' fill='none' xmlns='http://www.w3.org/2000/svg'>
      <path
        d='M16.5 14.25C16.5 14.6478 16.342 15.0294 16.0607 15.3107C15.7794 15.592 15.3978 15.75 15 15.75H3C2.60218 15.75 2.22064 15.592 1.93934 15.3107C1.65804 15.0294 1.5 14.6478 1.5 14.25V3.75C1.5 3.35218 1.65804 2.97064 1.93934 2.68934C2.22064 2.40804 2.60218 2.25 3 2.25H6.75L8.25 4.5H15C15.3978 4.5 15.7794 4.65804 16.0607 4.93934C16.342 5.22064 16.5 5.60218 16.5 6V14.25Z'
        stroke='white'
        strokeWidth='1.5'
        strokeLinecap='round'
        strokeLinejoin='round'
      />
    </svg>
  </div>
)

const ImageIcon = () => (
  <div className='flex items-center justify-center rounded-lg bg-tokens-status-warning-1 p-1'>
    <svg width='18' height='18' viewBox='0 0 18 18' fill='none' xmlns='http://www.w3.org/2000/svg'>
      <path
        fillRule='evenodd'
        clipRule='evenodd'
        d='M3.375 3.375C2.75368 3.375 2.25 3.87868 2.25 4.5V13.5C2.25 14.1213 2.75368 14.625 3.375 14.625H14.625C15.2463 14.625 15.75 14.1213 15.75 13.5V4.5C15.75 3.87868 15.2463 3.375 14.625 3.375H3.375ZM1.125 4.5C1.125 3.25736 2.13236 2.25 3.375 2.25H14.625C15.8676 2.25 16.875 3.25736 16.875 4.5V13.5C16.875 14.7426 15.8676 15.75 14.625 15.75H3.375C2.13236 15.75 1.125 14.7426 1.125 13.5V4.5Z'
        fill='white'
      />
      <path
        fillRule='evenodd'
        clipRule='evenodd'
        d='M11.8125 5.625C11.5018 5.625 11.25 5.87684 11.25 6.1875C11.25 6.49816 11.5018 6.75 11.8125 6.75C12.1232 6.75 12.375 6.49816 12.375 6.1875C12.375 5.87684 12.1232 5.625 11.8125 5.625ZM10.125 6.1875C10.125 5.25552 10.8805 4.5 11.8125 4.5C12.7445 4.5 13.5 5.25552 13.5 6.1875C13.5 7.11948 12.7445 7.875 11.8125 7.875C10.8805 7.875 10.125 7.11948 10.125 6.1875Z'
        fill='white'
      />
      <path
        fillRule='evenodd'
        clipRule='evenodd'
        d='M5.58449 8.1575C5.90619 7.8719 6.32482 7.71993 6.75482 7.73267C7.18466 7.74541 7.59342 7.92177 7.89761 8.22573C7.89764 8.22575 7.89766 8.22577 7.89768 8.22579C7.89777 8.22588 7.89785 8.22597 7.89794 8.22606L11.0849 11.407C11.3048 11.6265 11.3051 11.9826 11.0856 12.2025C10.8662 12.4224 10.51 12.4227 10.2901 12.2033L7.10254 9.02164C7.00113 8.92026 6.86483 8.86143 6.7215 8.85718C6.57819 8.85293 6.43868 8.90357 6.33145 8.99873C6.33143 8.99875 6.33141 8.99877 6.33139 8.99879L2.06126 12.7954C1.8291 13.0018 1.47355 12.9809 1.26713 12.7488C1.06071 12.5166 1.08158 12.1611 1.31375 11.9546L5.58449 8.1575Z'
        fill='white'
      />
      <path
        fillRule='evenodd'
        clipRule='evenodd'
        d='M13.3669 11.2133C13.259 11.1237 13.1215 11.0776 12.9814 11.0841C12.8412 11.0906 12.7085 11.1493 12.6093 11.2486L8.27275 15.5852C8.05308 15.8049 7.69692 15.8049 7.47725 15.5852C7.25758 15.3656 7.25758 15.0094 7.47725 14.7897L11.813 10.454C11.8131 10.4539 11.8132 10.4538 11.8133 10.4537C12.1107 10.1558 12.5087 9.97982 12.9293 9.96031C13.3499 9.9408 13.7626 10.0793 14.0864 10.3485L16.6728 12.5055C16.9114 12.7045 16.9435 13.0592 16.7445 13.2977C16.5455 13.5363 16.1908 13.5684 15.9522 13.3695L13.3671 11.2135C13.367 11.2134 13.3669 11.2134 13.3669 11.2133Z'
        fill='white'
      />
    </svg>
  </div>
)

const FileIcon = () => (
  <div className='flex items-center justify-center rounded-lg bg-tokens-status-error-1 p-1'>
    <svg width='18' height='18' viewBox='0 0 18 18' fill='none' xmlns='http://www.w3.org/2000/svg'>
      <path
        d='M10.5 1.5H4.5C4.10218 1.5 3.72064 1.65804 3.43934 1.93934C3.15804 2.22064 3 2.60218 3 3V15C3 15.3978 3.15804 15.7794 3.43934 16.0607C3.72064 16.342 4.10218 16.5 4.5 16.5H13.5C13.8978 16.5 14.2794 16.342 14.5607 16.0607C14.842 15.7794 15 15.3978 15 15V6L10.5 1.5Z'
        stroke='white'
        strokeWidth='1.5'
        strokeLinecap='round'
        strokeLinejoin='round'
      />
      <path
        d='M10.5 1.5V6H15'
        stroke='white'
        strokeWidth='1.5'
        strokeLinecap='round'
        strokeLinejoin='round'
      />
      <path
        d='M12 9.75H6'
        stroke='white'
        strokeWidth='1.5'
        strokeLinecap='round'
        strokeLinejoin='round'
      />
      <path
        d='M12 12.75H6'
        stroke='white'
        strokeWidth='1.5'
        strokeLinecap='round'
        strokeLinejoin='round'
      />
      <path
        d='M7.5 6.75H6.75H6'
        stroke='white'
        strokeWidth='1.5'
        strokeLinecap='round'
        strokeLinejoin='round'
      />
    </svg>
  </div>
)

const EditableIcon = () => (
  <div className='flex items-center justify-center rounded-[8px] bg-tokens-status-link-base size-[32px]'>
    <svg
      width='18'
      height='18'
      viewBox='0 0 24 24'
      strokeWidth='1.5'
      fill='none'
      xmlns='http://www.w3.org/2000/svg'
    >
      <path
        d='M20 12V5.74853C20 5.5894 19.9368 5.43679 19.8243 5.32426L16.6757 2.17574C16.5632 2.06321 16.4106 2 16.2515 2H4.6C4.26863 2 4 2.26863 4 2.6V21.4C4 21.7314 4.26863 22 4.6 22H11'
        stroke='#5B5BD5'
        strokeLinecap='round'
        strokeLinejoin='round'
      />
      <path
        d='M8 10H16M8 6H12M8 14H11'
        stroke='#5B5BD5'
        strokeLinecap='round'
        strokeLinejoin='round'
      />
      <path
        d='M17.9541 16.9394L18.9541 15.9394C19.392 15.5015 20.102 15.5015 20.5399 15.9394C20.9778 16.3773 20.9778 17.0873 20.5399 17.5252L19.5399 18.5252M17.9541 16.9394L14.963 19.9305C14.8131 20.0804 14.7147 20.2741 14.6821 20.4835L14.4394 22.0399L15.9957 21.7973C16.2052 21.7646 16.3988 21.6662 16.5487 21.5163L19.5399 18.5252M17.9541 16.9394L19.5399 18.5252'
        stroke='#5B5BD5'
        strokeLinecap='round'
        strokeLinejoin='round'
      />
      <path
        d='M16 2V5.4C16 5.73137 16.2686 6 16.6 6H20'
        stroke='#5B5BD5'
        strokeLinecap='round'
        strokeLinejoin='round'
      />
    </svg>
  </div>
)

export default CanvasMessageCard
